import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { pbdqFont } from '../theme/pbdqTokens';
import PbdqImpactStoryCard from './PbdqImpactStoryCard.comp';

const useStyles = makeStyles(() => ({
    container: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(56px, 7vw, 96px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    kicker: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
        fontFamily: pbdqFont,
    },
    headerText: {
        display: 'grid',
        gap: 12,
    },
    title: {
        fontSize: 'clamp(28px, 3.4vw, 46px)',
        lineHeight: 1.06,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
        '@media (max-width: 600px)': {
            whiteSpace: 'normal',
        },
    },
    titleHighlight: {
        color: '#ED6628',
    },
    lead: {
        fontSize: 16,
        lineHeight: 1.55,
        margin: 0,
        color: '#5B6470',
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
        '@media (max-width: 600px)': {
            whiteSpace: 'normal',
        },
    },

    filterRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 28,
    },
    filterBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 8px 8px 14px',
        borderRadius: 999,
        fontSize: 13.5,
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
        transition: 'background 200ms cubic-bezier(.32,.72,0,1), border-color 200ms cubic-bezier(.32,.72,0,1)',
        boxSizing: 'border-box',
        outline: 'none',
        '&:hover': {
            borderColor: '#1B2430',
        },
    },
    filterCount: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 22,
        height: 22,
        padding: '0 6px',
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
    },

    storiesList: {
        display: 'grid',
        gap: 'clamp(16px, 2vw, 24px)',
        marginTop: 36,
    },
}));

const CATEGORIES = [
    'All stories',
    'Governments and ministries',
    'Schools and universities',
    'Companies',
    'Frameworks and indices',
];

const CAT_COLORS = {
    'Governments and ministries': '#68C3EE',
    'Schools and universities': '#BCD253',
    'Companies': '#EDB459',
    'Frameworks and indices': '#B9316D',
};

const PbdqImpactStories = ({ stories }) => {
    const classes = useStyles();
    const [filter, setFilter] = useState('All stories');

    const visibleStories = filter === 'All stories'
        ? stories
        : stories.filter((s) => s.cats && s.cats.includes(filter));

    return (
        <div className={classes.container}>
            <div style={{ display: 'grid' }}>
                <span className={classes.kicker}>Impact stories</span>
                <div className={classes.headerText}>
                    <h2 className={classes.title}>
                        Stories of <span className={classes.titleHighlight}>impact</span>
                    </h2>
                    <p className={classes.lead}>
                        Governments, schools, companies and foundations building on Powered by DQ.
                    </p>
                </div>
            </div>

            <div role="group" aria-label="Browse by" className={classes.filterRow}>
                {CATEGORIES.map((catName) => {
                    const isSelected = catName === filter;
                    const count = catName === 'All stories'
                        ? stories.length
                        : stories.filter((s) => s.cats && s.cats.includes(catName)).length;

                    return (
                        <button
                            key={catName}
                            type="button"
                            aria-pressed={isSelected}
                            onClick={() => setFilter(catName)}
                            className={classes.filterBtn}
                            style={{
                                background: isSelected ? '#1B2430' : '#FFFFFF',
                                color: isSelected ? '#FFFFFF' : '#1B2430',
                                border: `1px solid ${isSelected ? '#1B2430' : '#D5D9E0'}`,
                            }}
                        >
                            {catName}
                            <span
                                className={classes.filterCount}
                                style={{
                                    background: isSelected ? 'rgba(255,255,255,0.18)' : '#F4F6F9',
                                    color: isSelected ? '#FFFFFF' : '#5B6470',
                                }}
                            >
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className={classes.storiesList}>
                {visibleStories.map((story) => {
                    const primaryCat = (story.cats && story.cats[0]) || 'Frameworks and indices';
                    const catColor = CAT_COLORS[primaryCat] || '#B8C0CC';

                    return (
                        <PbdqImpactStoryCard
                            key={story.id}
                            {...story}
                            sectorLabel={primaryCat}
                            sectorColor={catColor}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default PbdqImpactStories;
